import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { instance } from "../../Const/ApiHeader";
import {
  ALL_SUBSCRIPTION,
  DELETE_USER,
  LIST_USERS,
  MANAGE_USER_SUBSCRIPTION,
} from "../../Const/ApiConst";
import {
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatValidity(validity) {
  if (validity === "0") return "7 days (trial)";
  if (validity === "x") return "365 days";
  if (!validity) return "-";
  return `${validity} days`;
}

function hoursLeftLabel(user) {
  if (!user?.isLive) return "-";
  const hours = Number(user.remainingHours);
  if (!Number.isFinite(hours) || hours < 0) return "-";
  return hours === 1 ? "1 hour left" : `${hours} hours left`;
}

function durationDaysFromValidity(validity) {
  if (validity === "0") return 7;
  if (validity === "x") return 365;
  const days = parseInt(validity, 10);
  return Number.isFinite(days) && days > 0 ? days : 0;
}

function toDateTimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

function addDaysToLocal(value, days) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + days);
  return toDateTimeLocal(date);
}

function previewHoursLeft(expiryLocal) {
  if (!expiryLocal) return "";
  const remaining = new Date(expiryLocal).getTime() - Date.now();
  if (Number.isNaN(remaining) || remaining <= 0) return "Would be expired";
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  return hours === 1 ? "1 hour left after save" : `${hours} hours left after save`;
}

function planOptionValue(plan) {
  return `${plan.plan_name}::${plan.planValidity}`;
}

const DATE_ADJUSTMENTS = [-30, -7, -1, 1, 7, 30];

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [stats, setStats] = useState({ total: 0, activeSubscriptions: 0 });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [manageTarget, setManageTarget] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planMode, setPlanMode] = useState("catalog");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [customPlanName, setCustomPlanName] = useState("Custom");
  const [customDays, setCustomDays] = useState("30");
  const [lastPaymentLocal, setLastPaymentLocal] = useState("");
  const [expiryLocal, setExpiryLocal] = useState("");
  const [manageLoading, setManageLoading] = useState("");
  const [manageError, setManageError] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await instance.get(LIST_USERS, {
        params: { page, limit, search, status: statusFilter },
      });
      const payload = response?.data || {};
      setUsers(payload.data || []);
      setPagination(
        payload.pagination || {
          page,
          limit,
          total: 0,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false,
        }
      );
      setStats(payload.stats || { total: 0, activeSubscriptions: 0 });
    } catch (error) {
      console.log(error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const rangeLabel = useMemo(() => {
    if (!pagination.total) return "0 users";
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(pagination.page * pagination.limit, pagination.total);
    return `${start}–${end} of ${pagination.total}`;
  }, [pagination]);

  const closeDeleteDialog = () => {
    if (!deleteLoading) {
      setDeleteTarget(null);
      setDeleteError("");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.userID) return;
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await instance.post(DELETE_USER, { userID: deleteTarget.userID });
      setDeleteTarget(null);
      await loadUsers();
    } catch (error) {
      console.log(error);
      setDeleteError(
        error?.response?.data?.message ||
          "Failed to delete user. Please try again."
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const selectedValidityDays = useMemo(() => {
    if (planMode === "custom") {
      return durationDaysFromValidity(customDays);
    }
    if (!selectedPlan) return 0;
    const validity = selectedPlan.split("::")[1];
    return durationDaysFromValidity(validity);
  }, [planMode, customDays, selectedPlan]);

  const syncExpiryFromPayment = (paymentValue, days = selectedValidityDays) => {
    if (!paymentValue || !days) {
      setExpiryLocal("");
      return;
    }
    setExpiryLocal(addDaysToLocal(paymentValue, days));
  };

  const syncPaymentFromExpiry = (expiryValue, days = selectedValidityDays) => {
    if (!expiryValue || !days) {
      setLastPaymentLocal("");
      return;
    }
    setLastPaymentLocal(addDaysToLocal(expiryValue, -days));
  };

  const openManage = async (user) => {
    setManageError("");
    setManageTarget(user);
    setCustomPlanName(user.subscriptionPlan || "Custom");
    const currentDays = durationDaysFromValidity(user.subscriptionDuration);
    setCustomDays(String(currentDays || 30));
    setLastPaymentLocal(
      toDateTimeLocal(user.lastPaymentDate) || toDateTimeLocal(new Date())
    );
    setExpiryLocal(
      toDateTimeLocal(user.expiryDate) ||
        addDaysToLocal(
          toDateTimeLocal(user.lastPaymentDate) || toDateTimeLocal(new Date()),
          currentDays || 30
        )
    );
    try {
      const response = await instance.get(ALL_SUBSCRIPTION);
      const list = response?.data || [];
      setPlans(list);
      const current = list.find(
        (plan) =>
          plan.plan_name === user.subscriptionPlan &&
          String(plan.planValidity) === String(user.subscriptionDuration)
      );
      if (current) {
        setPlanMode("catalog");
        setSelectedPlan(planOptionValue(current));
      } else if (user.subscriptionPlan) {
        setPlanMode("custom");
        setSelectedPlan(list[0] ? planOptionValue(list[0]) : "");
      } else if (list[0]) {
        setPlanMode("catalog");
        setSelectedPlan(planOptionValue(list[0]));
        const days = durationDaysFromValidity(list[0].planValidity);
        const payment = toDateTimeLocal(new Date());
        setLastPaymentLocal(payment);
        setExpiryLocal(addDaysToLocal(payment, days));
      } else {
        setPlanMode("custom");
        setSelectedPlan("");
      }
    } catch (error) {
      console.log(error);
      setPlanMode("custom");
      setManageError("Failed to load subscription plans.");
    }
  };

  const closeManage = () => {
    if (manageLoading) return;
    setManageTarget(null);
    setManageError("");
    setSelectedPlan("");
    setPlanMode("catalog");
  };

  const resolvePlanPayload = () => {
    if (planMode === "custom") {
      const days = durationDaysFromValidity(customDays);
      if (!customPlanName.trim() || !days) {
        return { error: "Enter a custom plan name and a duration in days." };
      }
      return {
        planName: customPlanName.trim(),
        planValidity: String(days),
      };
    }
    if (!selectedPlan) {
      return { error: "Select a catalog plan." };
    }
    const [planName, planValidity] = selectedPlan.split("::");
    return { planName, planValidity };
  };

  const handleManage = async (action) => {
    if (!manageTarget?.userID) return;

    const plan = action === "expire" ? {} : resolvePlanPayload();
    if (plan.error) {
      setManageError(plan.error);
      return;
    }
    if (action !== "expire" && !lastPaymentLocal) {
      setManageError("Set a last payment date.");
      return;
    }

    setManageLoading(action);
    setManageError("");
    try {
      const payload = { userID: manageTarget.userID, action };
      if (action !== "expire") {
        payload.planName = plan.planName;
        payload.planValidity = plan.planValidity;
        payload.lastPaymentDate = new Date(lastPaymentLocal).toISOString();
        if (expiryLocal) {
          payload.expiryDate = new Date(expiryLocal).toISOString();
        }
      }
      await instance.post(MANAGE_USER_SUBSCRIPTION, payload);
      setManageTarget(null);
      await loadUsers();
    } catch (error) {
      console.log(error);
      setManageError(
        error?.response?.data?.message ||
          "Failed to update subscription. Please try again."
      );
    } finally {
      setManageLoading("");
    }
  };

  return (
    <div className="vehicles-page">
      <Breadcrumbs aria-label="breadcrumb" className="mb-3">
        <Link to="/">Home</Link>
        <Typography>Users</Typography>
      </Breadcrumbs>

      <section className="vehicles-hero">
        <div>
          <Typography className="vehicles-hero-eyebrow">
            <PeopleOutlinedIcon fontSize="inherit" /> App accounts
          </Typography>
          <Typography variant="h4" className="vehicles-hero-title">
            Users
          </Typography>
          <Typography className="vehicles-hero-subtitle">
            Search registered app users, grant or expire subscriptions, and
            permanently delete accounts.
          </Typography>
        </div>
      </section>

      <section className="vehicles-stats-grid">
        <div className="vehicles-stat-card">
          <Typography className="vehicles-stat-value">{stats.total}</Typography>
          <Typography className="vehicles-stat-label">Matching users</Typography>
        </div>
        <div className="vehicles-stat-card tone-green">
          <Typography className="vehicles-stat-value">
            {stats.activeSubscriptions}
          </Typography>
          <Typography className="vehicles-stat-label">
            Active subscriptions
          </Typography>
        </div>
        <div className="vehicles-stat-card tone-blue">
          <Typography className="vehicles-stat-value">{users.length}</Typography>
          <Typography className="vehicles-stat-label">On this page</Typography>
        </div>
      </section>

      <section className="section vehicles-toolbar">
        <Form.Control
          className="admin-search"
          type="search"
          placeholder="Search by name, email, phone, or user ID..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <Form.Select
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
          style={{ maxWidth: 180 }}
        >
          <option value="active">Active only</option>
          <option value="inactive">Inactive</option>
          <option value="all">All users</option>
        </Form.Select>
      </section>

      <div className="section">
        <table className="table admin-table mt-0">
          <thead>
            <tr>
              <th>User</th>
              <th>Contact</th>
              <th>Plan</th>
              <th>Expires</th>
              <th>Status</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-4">
                  <CircularProgress size={28} />
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-secondary">
                  {stats.total === 0 && !search
                    ? statusFilter === "active"
                      ? "No active subscribers."
                      : "No users registered yet."
                    : "No users match your search."}
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user._id || user.userID}>
                  <td>
                    <div>{user.fullName || "-"}</div>
                    <div className="text-secondary small">{user.email || "-"}</div>
                    <div className="text-secondary small">{user.userID}</div>
                  </td>
                  <td>{user.contactNo || "-"}</td>
                  <td>
                    <div>{user.subscriptionPlan || "-"}</div>
                    <div className="text-secondary small">
                      {formatValidity(user.subscriptionDuration)}
                    </div>
                  </td>
                  <td>
                    <div>{formatDateTime(user.expiryDate)}</div>
                    <div className="text-secondary small">
                      {hoursLeftLabel(user)}
                    </div>
                  </td>
                  <td>
                    <Chip
                      size="small"
                      label={user.isLive ? "Active sub" : "Inactive"}
                      color={user.isLive ? "success" : "default"}
                    />
                  </td>
                  <td>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => openManage(user)}
                      aria-label={`Manage subscription for ${
                        user.fullName || user.email || "user"
                      }`}
                    >
                      <CardMembershipOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setDeleteError("");
                        setDeleteTarget(user);
                      }}
                      aria-label={`Delete ${user.fullName || user.email || "user"}`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="admin-pagination">
          <Typography className="admin-pagination-range">{rangeLabel}</Typography>
          <div className="admin-pagination-controls">
            <IconButton
              size="small"
              disabled={!pagination.hasPrevPage || loading}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              aria-label="Previous page"
            >
              <ChevronLeftIcon />
            </IconButton>
            <Pagination
              count={pagination.totalPages}
              page={pagination.page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size="small"
              siblingCount={1}
              boundaryCount={1}
              disabled={loading}
            />
            <IconButton
              size="small"
              disabled={!pagination.hasNextPage || loading}
              onClick={() =>
                setPage((prev) => Math.min(pagination.totalPages, prev + 1))
              }
              aria-label="Next page"
            >
              <ChevronRightIcon />
            </IconButton>
          </div>
          <div className="admin-pagination-size">
            <Typography variant="body2" color="text.secondary">
              Rows
            </Typography>
            <Select
              size="small"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              disabled={loading}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>
      </div>

      <Modal show={Boolean(deleteTarget)} onHide={closeDeleteDialog} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete user</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Typography>
            Permanently delete{" "}
            <strong>
              {deleteTarget?.fullName ||
                deleteTarget?.email ||
                deleteTarget?.userID}
            </strong>
            ? This removes their account, subscription records, and related
            billing data. This cannot be undone.
          </Typography>
          {deleteError ? (
            <Typography color="error" className="mt-2">
              {deleteError}
            </Typography>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outlined"
            onClick={closeDeleteDialog}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            color="error"
            variant="contained"
            loading={deleteLoading}
            onClick={handleDelete}
          >
            Delete user
          </LoadingButton>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(manageTarget)} onHide={closeManage} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Manage subscription</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Typography className="mb-3">
            <strong>
              {manageTarget?.fullName ||
                manageTarget?.email ||
                manageTarget?.userID}
            </strong>
          </Typography>
          <div className="mb-3 text-secondary small">
            <div>Current plan: {manageTarget?.subscriptionPlan || "-"}</div>
            <div>
              Duration: {formatValidity(manageTarget?.subscriptionDuration)}
            </div>
            <div>
              Last payment: {formatDateTime(manageTarget?.lastPaymentDate)}
            </div>
            <div>Expires: {formatDateTime(manageTarget?.expiryDate)}</div>
            <div>{hoursLeftLabel(manageTarget)}</div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Plan type</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id="plan-catalog"
                label="Catalog plan"
                checked={planMode === "catalog"}
                disabled={Boolean(manageLoading)}
                onChange={() => {
                  setPlanMode("catalog");
                  if (selectedPlan) {
                    const days = durationDaysFromValidity(
                      selectedPlan.split("::")[1]
                    );
                    syncExpiryFromPayment(lastPaymentLocal, days);
                  }
                }}
              />
              <Form.Check
                type="radio"
                id="plan-custom"
                label="Custom plan"
                checked={planMode === "custom"}
                disabled={Boolean(manageLoading)}
                onChange={() => {
                  setPlanMode("custom");
                  syncExpiryFromPayment(
                    lastPaymentLocal,
                    durationDaysFromValidity(customDays)
                  );
                }}
              />
            </div>
          </Form.Group>

          {planMode === "catalog" ? (
            <Form.Group className="mb-3">
              <Form.Label>Catalog plan</Form.Label>
              <Form.Select
                value={selectedPlan}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedPlan(value);
                  const days = durationDaysFromValidity(value.split("::")[1]);
                  syncExpiryFromPayment(lastPaymentLocal, days);
                }}
                disabled={Boolean(manageLoading)}
              >
                {plans.length === 0 ? (
                  <option value="">No plans available</option>
                ) : (
                  plans.map((plan) => (
                    <option key={plan._id} value={planOptionValue(plan)}>
                      {plan.plan_name} · {formatValidity(plan.planValidity)}
                      {plan.isHidden ? " (hidden)" : ""}
                    </option>
                  ))
                )}
              </Form.Select>
            </Form.Group>
          ) : (
            <div className="row g-3 mb-3">
              <div className="col-md-8">
                <Form.Group>
                  <Form.Label>Custom plan name</Form.Label>
                  <Form.Control
                    type="text"
                    value={customPlanName}
                    onChange={(e) => setCustomPlanName(e.target.value)}
                    disabled={Boolean(manageLoading)}
                    placeholder="e.g. Complimentary 45 days"
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Duration (days)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={customDays}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCustomDays(value);
                      syncExpiryFromPayment(
                        lastPaymentLocal,
                        durationDaysFromValidity(value)
                      );
                    }}
                    disabled={Boolean(manageLoading)}
                  />
                </Form.Group>
              </div>
            </div>
          )}

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Last payment</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={lastPaymentLocal}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLastPaymentLocal(value);
                    syncExpiryFromPayment(value);
                  }}
                  disabled={Boolean(manageLoading)}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Expires</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={expiryLocal}
                  onChange={(e) => {
                    const value = e.target.value;
                    setExpiryLocal(value);
                    syncPaymentFromExpiry(value);
                  }}
                  disabled={Boolean(manageLoading)}
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-2">
            <Form.Label>Adjust expiry</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {DATE_ADJUSTMENTS.map((days) => (
                <Button
                  key={days}
                  size="small"
                  variant="outlined"
                  disabled={Boolean(manageLoading) || !expiryLocal}
                  onClick={() => {
                    const nextExpiry = addDaysToLocal(expiryLocal, days);
                    setExpiryLocal(nextExpiry);
                    syncPaymentFromExpiry(nextExpiry);
                  }}
                >
                  {days > 0 ? `+${days}d` : `${days}d`}
                </Button>
              ))}
            </div>
          </Form.Group>
          <Typography variant="body2" color="text.secondary">
            {previewHoursLeft(expiryLocal)}
          </Typography>

          {manageError ? (
            <Typography color="error" className="mt-2">
              {manageError}
            </Typography>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outlined"
            onClick={closeManage}
            disabled={Boolean(manageLoading)}
          >
            Cancel
          </Button>
          {manageTarget?.isLive ? (
            <LoadingButton
              color="warning"
              variant="outlined"
              loading={manageLoading === "expire"}
              disabled={Boolean(manageLoading)}
              onClick={() => handleManage("expire")}
            >
              Expire
            </LoadingButton>
          ) : null}
          <LoadingButton
            variant="contained"
            loading={manageLoading === "save"}
            disabled={Boolean(manageLoading)}
            onClick={() => handleManage("save")}
          >
            Save subscription
          </LoadingButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Users;
